import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PaymentAccount, PaymentAccountFormData } from '@/types/payment-account';
import { bankLogoService } from '@/services/bankLogoService';

interface UsePaymentAccountMutationsReturn {
  createAccount: (data: PaymentAccountFormData) => Promise<PaymentAccount | null>;
  updateAccount: (id: string, data: Partial<PaymentAccountFormData>) => Promise<PaymentAccount | null>;
  deleteAccount: (id: string) => Promise<boolean>;
  loading: boolean;
  error: string | null;
}

export const usePaymentAccountMutations = (): UsePaymentAccountMutationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const createAccount = useCallback(async (data: PaymentAccountFormData): Promise<PaymentAccount | null> => {
    try {
      setLoading(true);
      clearError();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      const now = new Date().toISOString();

      // Get logo URL for the bank if bank_name is provided
      let logoUrl: string | null = null;
      if (data.bank_name) {
        try {
          const fetchedLogoUrl = await bankLogoService.getLogoUrlForBank(data.bank_name);
          logoUrl = fetchedLogoUrl || null;
        } catch (error) {
          console.warn('Could not fetch logo for bank:', data.bank_name, error);
        }
      }

      // Prepare data for database - aligning with current schema
      const dbData: Record<string, any> = {
        name: data.name,
        description: data.description ?? null,
        type: data.type,
        bank_name: data.bank_name ?? null,
        account_number: data.account_number ?? null,
        agency: data.agency ?? null,
        initial_balance: data.initial_balance,
        current_balance: data.initial_balance,
        status: data.status,
        color: data.color ?? '#3b82f6',
        user_id: user.id,
        is_default: data.is_default,
        is_active: true,
        created_at: now,
        updated_at: now,
      };

      if (logoUrl) {
        dbData.logo_url = logoUrl;
      }

      const { data: insertData, error: insertError } = await supabase
        .from('payment_accounts')
        .insert(dbData)
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Transform to PaymentAccount interface - incluindo logo_url
      const transformedAccount: PaymentAccount = {
        id: insertData.id,
        name: insertData.name,
        description: insertData.description,
        type: insertData.type as any,
        bank_name: insertData.bank_name,
        account_number: insertData.account_number,
        agency: insertData.agency,
        initial_balance: Number(insertData.initial_balance ?? 0),
        current_balance: Number(insertData.current_balance ?? insertData.initial_balance ?? 0),
        balance: Number(insertData.current_balance ?? insertData.initial_balance ?? 0),
        status: insertData.status as any || 'active',
        color: insertData.color || '#3b82f6',
        logo_url: insertData.logo_url ?? undefined,
        user_id: insertData.user_id,
        created_at: insertData.created_at ? new Date(insertData.created_at) : new Date(),
        updated_at: insertData.updated_at ? new Date(insertData.updated_at) : new Date(),
        is_active: insertData.is_active ?? true,
        is_default: insertData.is_default ?? false,
        transaction_count: insertData.transaction_count ?? 0,
      };

      return transformedAccount;
    } catch (err) {
      console.error('Erro ao criar conta:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar conta';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  const updateAccount = useCallback(async (id: string, data: Partial<PaymentAccountFormData>): Promise<PaymentAccount | null> => {
    try {
      setLoading(true);
      clearError();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Prepare data for database - incluindo logo_url se o banco mudar
      const dbData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (data.name !== undefined) dbData.name = data.name;
      if (data.description !== undefined) dbData.description = data.description ?? null;
      if (data.type !== undefined) dbData.type = data.type;
      if (data.initial_balance !== undefined) dbData.initial_balance = data.initial_balance;
      if (data.status !== undefined) dbData.status = data.status;
      if (data.color !== undefined) dbData.color = data.color ?? null;
      if (data.account_number !== undefined) dbData.account_number = data.account_number ?? null;
      if (data.agency !== undefined) dbData.agency = data.agency ?? null;
      if (data.is_default !== undefined) dbData.is_default = data.is_default;

      // Se bank_name mudou, buscar novo logo
      if (data.bank_name !== undefined) {
        dbData.bank_name = data.bank_name;
        try {
          const fetchedLogoUrl = await bankLogoService.getLogoUrlForBank(data.bank_name);
          if (fetchedLogoUrl) {
            dbData.logo_url = fetchedLogoUrl;
          }
        } catch (error) {
          console.warn('Could not fetch logo for bank:', data.bank_name, error);
        }
      }

      const { data: updatedData, error: updateError } = await supabase
        .from('payment_accounts')
        .update(dbData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      // Transform to PaymentAccount interface - incluindo logo_url
      const transformedAccount: PaymentAccount = {
        id: updatedData.id,
        name: updatedData.name,
        description: updatedData.description,
        type: updatedData.type as any,
        bank_name: updatedData.bank_name,
        account_number: updatedData.account_number,
        agency: updatedData.agency,
        initial_balance: Number(updatedData.initial_balance || 0),
        current_balance: Number(updatedData.current_balance ?? updatedData.initial_balance ?? 0),
        balance: Number(updatedData.current_balance ?? updatedData.initial_balance ?? 0),
        status: updatedData.status as any,
        color: updatedData.color, // mantido para compatibilidade
        logo_url: updatedData.logo_url ?? undefined,
        user_id: updatedData.user_id,
        created_at: updatedData.created_at ? new Date(updatedData.created_at) : new Date(),
        updated_at: updatedData.updated_at ? new Date(updatedData.updated_at) : new Date(),
        is_active: updatedData.is_active ?? true,
        is_default: updatedData.is_default ?? false,
        transaction_count: updatedData.transaction_count ?? 0,
      };

      return transformedAccount;
    } catch (err) {
      console.error('Erro ao atualizar conta:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar conta';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  const deleteAccount = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      clearError();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      const { error: deleteError } = await supabase
        .from('payment_accounts')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) {
        throw deleteError;
      }

      return true;
    } catch (err) {
      console.error('Erro ao excluir conta:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir conta';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  return {
    createAccount,
    updateAccount,
    deleteAccount,
    loading,
    error,
  };
};
